/**
 * Gesture handlers for BaseModal (swipe, drag, pull-to-refresh)
 */

import {triggerHaptic} from './animations.js';

/**
 * Swipe navigation - Start
 */
export function onSwipeStart(modal, event) {
    if (!modal.config.swipeNavigation) return;

    const touch      = event.touches[0];
    modal.swipeStartX = touch.clientX;
    modal.swipeStartY = touch.clientY;
    modal.isSwiping   = true;
}

/**
 * Swipe navigation - Move
 */
export function onSwipeMove(modal, event) {
    if (!modal.isSwiping) return;

    const touch        = event.touches[0];
    modal.swipeCurrentX = touch.clientX;
    modal.swipeCurrentY = touch.clientY;
}

/**
 * Swipe navigation - End
 */
export function onSwipeEnd(modal, event) {
    if (!modal.isSwiping) return;

    const deltaX = modal.swipeCurrentX - modal.swipeStartX;
    const deltaY = modal.swipeCurrentY - modal.swipeStartY;

    // Check if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > modal.swipeThreshold) {
        if (deltaX > 0) {
            // Swipe right
            triggerHaptic(modal, 'light');
            modal.config.onSwipeRight.call(modal, modal.modal, event);
            modal.emit('swipeRight', {
                modal:modal.modal,
                event
            });
        } else {
            // Swipe left
            triggerHaptic(modal, 'light');
            modal.config.onSwipeLeft.call(modal, modal.modal, event);
            modal.emit('swipeLeft', {
                modal:modal.modal,
                event
            });
        }
    }

    // Reset state
    modal.isSwiping     = false;
    modal.swipeStartX   = 0;
    modal.swipeStartY   = 0;
    modal.swipeCurrentX = 0;
    modal.swipeCurrentY = 0;
}

/**
 * Mouse drag - Start (for desktop swipe)
 */
export function onMouseDragStart(modal, event) {
    if (!modal.config.swipeNavigation) return;

    modal.swipeStartX = event.clientX;
    modal.swipeStartY = event.clientY;
    modal.isSwiping   = true;

    // Change cursor
    event.currentTarget.style.cursor     = 'grabbing';
    event.currentTarget.style.userSelect = 'none';

    // Prevent default to avoid text selection
    event.preventDefault();
}

/**
 * Mouse drag - Move
 */
export function onMouseDragMove(modal, event) {
    if (!modal.isSwiping) return;

    modal.swipeCurrentX = event.clientX;
    modal.swipeCurrentY = event.clientY;
}

/**
 * Mouse drag - End
 */
export function onMouseDragEnd(modal, event) {
    if (!modal.isSwiping) return;

    const deltaX = modal.swipeCurrentX - modal.swipeStartX;
    const deltaY = modal.swipeCurrentY - modal.swipeStartY;

    // Reset cursor
    if (event.currentTarget) {
        event.currentTarget.style.cursor     = '';
        event.currentTarget.style.userSelect = '';
    }

    // Check if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > modal.swipeThreshold) {
        if (deltaX > 0) {
            // Swipe right
            triggerHaptic(modal, 'light');
            modal.config.onSwipeRight.call(modal, modal.modal, event);
            modal.emit('swipeRight', {
                modal:modal.modal,
                event
            });
        } else {
            // Swipe left
            triggerHaptic(modal, 'light');
            modal.config.onSwipeLeft.call(modal, modal.modal, event);
            modal.emit('swipeLeft', {
                modal:modal.modal,
                event
            });
        }
    }

    // Reset state
    modal.isSwiping     = false;
    modal.swipeStartX   = 0;
    modal.swipeStartY   = 0;
    modal.swipeCurrentX = 0;
    modal.swipeCurrentY = 0;
}

/**
 * Pull to refresh - Start
 */
export function onPullStart(modal, event) {
    if (!modal.config.pullToRefresh || modal.isRefreshing) return;

    const container = modal.modal.querySelector('.bm-modal__content');
    if (!container) return;

    // Only trigger if scrolled to top
    if (container.scrollTop === 0) {
        const touch     = event.touches[0];
        modal.pullStartY = touch.clientY;
        modal.isPulling  = true;
    }
}

/**
 * Pull to refresh - Move
 */
export function onPullMove(modal, event) {
    if (!modal.isPulling || modal.isRefreshing) return;

    const touch       = event.touches[0];
    modal.pullCurrentY = touch.clientY;
    const delta       = modal.pullCurrentY - modal.pullStartY;

    if (delta > 0) {
        // Add visual feedback
        const container = modal.modal.querySelector('.bm-modal__content');
        if (container) {
            container.style.transform  = `translateY(${Math.min(delta, modal.pullThreshold)}px)`;
            container.style.transition = 'none';
        }
    }
}

/**
 * Pull to refresh - End
 */
export async function onPullEnd(modal, event) {
    if (!modal.isPulling) return;

    const delta     = modal.pullCurrentY - modal.pullStartY;
    const container = modal.modal.querySelector('.bm-modal__content');

    if (delta >= modal.pullThreshold) {
        // Trigger refresh
        modal.isRefreshing = true;
        triggerHaptic(modal, 'medium');

        try {
            await modal.config.onRefresh.call(modal, modal.modal, event);
            triggerHaptic(modal, 'success');
        } catch (error) {
            triggerHaptic(modal, 'error');
            console.error('BaseModal: Refresh error', error);
        } finally {
            modal.isRefreshing = false;
        }
    }

    // Reset visual state
    if (container) {
        container.style.transform  = '';
        container.style.transition = '';
    }

    // Reset state
    modal.isPulling    = false;
    modal.pullStartY   = 0;
    modal.pullCurrentY = 0;
}

/**
 * Drag to dismiss - Start (touch)
 */
export function onDragStart(modal, e) {
    if (!modal.config.dragToDismiss) return;

    // Check if drag started on handle
    const handle = modal.modal.querySelector('.bm-modal__drag-handle');
    if (handle && (e.target === handle || handle.contains(e.target))) {
        modal.dragStartY = e.touches[0].clientY;
        modal.isDragging = true;
        modal.dragHandle = handle;
    }
}

/**
 * Drag to dismiss - Start (mouse)
 */
export function onDragStartMouse(modal, e) {
    if (!modal.config.dragToDismiss) return;

    // Check if drag started on handle
    const handle = modal.modal.querySelector('.bm-modal__drag-handle');
    if (handle && (e.target === handle || handle.contains(e.target))) {
        modal.dragStartY = e.clientY;
        modal.isDragging = true;
        modal.dragHandle = handle;

        // Change cursor
        handle.style.cursor = 'grabbing';

        // Prevent text selection
        e.preventDefault();
    }
}

/**
 * Drag to dismiss - Move (touch)
 */
export function onDragMove(modal, e) {
    if (!modal.isDragging || !modal.config.dragToDismiss) return;

    modal.dragCurrentY = e.touches[0].clientY;
    const diff        = modal.dragCurrentY - modal.dragStartY;

    // Only allow dragging down
    if (diff > 0) {
        const container = modal.modal.querySelector('.bm-modal__container');
        if (container) {
            container.style.transform  = `translateY(${diff}px)`;
            container.style.opacity    = Math.max(0.3, 1 - (diff / 300));
            container.style.transition = 'none';
        }
    }
}

/**
 * Drag to dismiss - Move (mouse)
 */
export function onDragMoveMouse(modal, e) {
    if (!modal.isDragging || !modal.config.dragToDismiss) return;

    modal.dragCurrentY = e.clientY;
    const diff        = modal.dragCurrentY - modal.dragStartY;

    // Only allow dragging down
    if (diff > 0) {
        const container = modal.modal.querySelector('.bm-modal__container');
        if (container) {
            container.style.transform  = `translateY(${diff}px)`;
            container.style.opacity    = Math.max(0.3, 1 - (diff / 300));
            container.style.transition = 'none';
        }
    }
}

/**
 * Drag to dismiss - End (touch & mouse)
 */
export function onDragEnd(modal, e) {
    if (!modal.isDragging || !modal.config.dragToDismiss) return;

    const diff      = modal.dragCurrentY - modal.dragStartY;
    const container = modal.modal.querySelector('.bm-modal__container');
    const handle    = modal.dragHandle;

    // Reset cursor
    if (handle) {
        handle.style.cursor = '';
    }

    if (container) {
        // If dragged beyond threshold, close modal WITHOUT animation
        if (diff > modal.config.dragThreshold) {
            // Remove animation class to prevent jump
            const animationClass = Array.from(modal.modal.classList).find(cls => cls.startsWith('bm-modal--animation-'));
            if (animationClass && typeof animationClass === 'string') {
                modal.modal.classList.remove(animationClass);
            }

            // Close modal
            modal.closeModal().then(() => {
                // Clean up inline styles after close
                container.style.transform  = '';
                container.style.opacity    = '';
                container.style.transition = '';

                // Restore animation class for next open
                if (animationClass && typeof animationClass === 'string') {
                    modal.modal.classList.add(animationClass);
                }
            });
        } else {
            // Reset position with transition
            container.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
            container.style.transform  = '';
            container.style.opacity    = '';

            // Clean up transition after animation
            setTimeout(() => {
                container.style.transition = '';
            }, 300);
        }
    }

    // Reset drag state
    modal.dragStartY   = 0;
    modal.dragCurrentY = 0;
    modal.isDragging   = false;
    modal.dragHandle   = null;
}

/**
 * Drag to dismiss - End (mouse specific)
 */
export function onDragEndMouse(modal, e) {
    onDragEnd(modal, e);
}
